import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {action, makeAutoObservable, observable, reaction} from "mobx";
import {type IChecksums, type IChecksumStore} from "data/stores/checksum/checksum.store";
import {type IContestsStore} from "data/stores/contests/contestsStore";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import type {ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IContestPlayerStatsStore} from "data/stores/contest_player_stats/contest_player_stats.store";
import type {IPlayerStatsStore} from "data/stores/player_stats/player_stats.store";
import type {IGameBarStore} from "data/stores/game_bar/game_bar.store";
import {compact, uniq} from "lodash";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";
import type {IPlayerCalculations} from "data/utils/player.calculations";
import type {IRoundSummaryStore} from "data/stores/round_summary/round_summary.store";
import {ChecksumsKeys} from "data/enums";
import type {IUserStore} from "data/stores/user/user.store";

/**
 * Constant for determine update frequency.
 */
const LIVE_SCORING_FETCH_TIMING = 1000 * 15;

type IChecksumAction = Record<keyof IChecksums, Array<() => Promise<void>>>;

export interface ILiveScoreController extends ViewController {
	subscribeLiveScoring: () => void;
	unsubscribeLiveScoring: () => void;
}

@injectable()
export class LiveScoreController implements ILiveScoreController {
	@observable protected _interval?: ReturnType<typeof setInterval>;
	@observable protected _isSubscribed: boolean = false;
	@observable protected _liveScoreDisposer?: ReturnType<typeof reaction>;

	get updatedChecksum(): IChecksums {
		return this._checksumStore.changedChecksums;
	}

	get isAuthorized(): boolean {
		return this._userStore.isAuthorized;
	}

	constructor(
		@inject(Bindings.ChecksumStore) private _checksumStore: IChecksumStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ContestsStore) private _contestsStore: IContestsStore,
		@inject(Bindings.PlayersStore) private _playersStore: IPlayersStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.TournamentsStore) private _tournamentsStore: ITournamentsStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.ContestPlayerStatsStore)
		private _contestPlayerStatsStore: IContestPlayerStatsStore,
		@inject(Bindings.PlayerStatsStore) private _playerStatsStore: IPlayerStatsStore,
		@inject(Bindings.PlayerCalculations) private _playerCalculations: IPlayerCalculations,
		@inject(Bindings.GameBarStore) private _gameBarStore: IGameBarStore,
		@inject(Bindings.RankingsStore) private _rankingsStore: IRankingsStore,
		@inject(Bindings.RoundSummaryStore) private _rosterSummaryStore: IRoundSummaryStore
	) {
		makeAutoObservable(this);
	}

	/**
	 * Call to subscribe on checksum changes
	 */
	@action
	public subscribeLiveScoring() {
		if (this._isSubscribed) {
			return;
		}

		this._isSubscribed = true;

		void this._checksumStore.fetchChecksums();

		this._interval = setInterval(() => {
			void this._checksumStore.fetchChecksums();
		}, LIVE_SCORING_FETCH_TIMING);
	}

	/**
	 * Stop checking changes
	 * called on dispose
	 * or you can call it when you want to stop listen checksums, for example on the end of the game match/round/etc.
	 */
	@action
	public unsubscribeLiveScoring() {
		this._isSubscribed = false;

		if (this._interval) {
			clearInterval(this._interval);
		}
	}

	/**
	 * Check changed checksums and call actions
	 */
	@action
	callActions = () => {
		const subscribes = this.generateActions;
		const actions = compact(
			Object.keys(this.updatedChecksum).flatMap((key) => {
				return subscribes[key as ChecksumsKeys];
			})
		);

		const callBacks = compact(
			Object.keys(this.updatedChecksum).flatMap((key) => {
				return this._checksumStore.subscriptions[key as ChecksumsKeys];
			})
		);
		void Promise.all(uniq([...actions, ...callBacks]).map((call) => call())).then(() => {
			this._playerCalculations.calculatePlayers();
		});
	};

	/**
	 * Provide object of files you want to update
	 * for example: rounds
	 */
	private get generateActions(): IChecksumAction {
		const liveContestId = this._checksumStore.liveContestId;
		const liveRoundId = this._checksumStore.liveRoundId;

		return {
			[ChecksumsKeys.contests]: [() => this._contestsStore.fetchContests()],
			[ChecksumsKeys.players]: [() => this._playersStore.fetchPlayers()],
			[ChecksumsKeys.squads]: [() => this._squadsStore.fetchSquads()],
			[ChecksumsKeys.tournaments]: [() => this._tournamentsStore.fetchTournaments()],
			[ChecksumsKeys.contestPlayerStats]: [
				() => this._contestPlayerStatsStore.fetchStatsLive(liveContestId),
				() =>
					this.isAuthorized
						? this._rosterSummaryStore.fetchSummary(liveRoundId)
						: Promise.resolve(),
			],
			[ChecksumsKeys.rounds]: [() => this._roundsStore.fetchRounds()],
			[ChecksumsKeys.playerStats]: [() => this._playerStatsStore.fetchStats()],
			...(this.isAuthorized
				? {
						[ChecksumsKeys.contestRankings]: [
							() => this._gameBarStore.fetchGameBarLive(),
							() => {
								this._rankingsStore.updateLastUpdated(new Date());
								return Promise.resolve();
							},
						],
						[ChecksumsKeys.roundRankings]: [
							() => this._gameBarStore.fetchGameBarLive(),
							() => {
								this._rankingsStore.updateLastUpdated(new Date());
								return Promise.resolve();
							},
						],
					}
				: {
						[ChecksumsKeys.contestRankings]: [],
						[ChecksumsKeys.roundRankings]: [],
					}),
		};
	}

	dispose(): void {
		this._liveScoreDisposer?.();
		this.unsubscribeLiveScoring();
	}

	async init() {
		// Fetch checksums
		await this._checksumStore.fetchChecksums();

		// Subscribe on changes
		this._liveScoreDisposer = reaction(() => this.updatedChecksum, this.callActions, {
			fireImmediately: true,
		});

		this.subscribeLiveScoring();
	}
}
